import { dateTimeDiff } from '@components/HelperComponents/HelperComponents';
import { t } from '@hclinear/hc-linear-react';
import { TerminalStatusBriefDTO, TerminalStatusWrapperDTO } from '@/model';
import { CommunicationLevel, CommunicationStatus, TranslationFunction } from '@/type';

const DELAY_FROM_MIN = 2;
const BREAK_FROM_MIN = 30;
const NOTHING_FROM_MIN = 1440;

const COMMUNICATION_COLOR: Record<CommunicationLevel, string> = {
  onTime: 'green',
  delay: 'orange',
  break: 'red',
  nothing: 'black',
};

const COMMUNICATION_TEXT_KEY: Record<CommunicationLevel, string> = {
  onTime: 'stop-terminal-status.stop-table.header.ok-time.on-time',
  delay: 'stop-terminal-status.stop-table.header.ok-time.delay',
  break: 'stop-terminal-status.stop-table.header.ok-time.break',
  nothing: 'stop-terminal-status.stop-table.header.ok-time.nothing',
};

const isLateLevel = (level: CommunicationLevel): boolean => level !== 'onTime';

const getCommunicationLevel = (diffMin: number): CommunicationLevel => {
  if (diffMin <= DELAY_FROM_MIN) {
    return 'onTime';
  }

  if (diffMin <= BREAK_FROM_MIN) {
    return 'delay';
  }

  if (diffMin <= NOTHING_FROM_MIN) {
    return 'break';
  }

  return 'nothing';
};

const getTimeDiff = (dateTime: string) =>
  dateTimeDiff(
    dateTime,
    t('general.day'),
    t('general.hour'),
    t('general.min'),
    t('general.sec'),
    t('general.now'),
  );

export const formatFullDateTime = (dateTime?: string): string =>
  dateTime ? dateTime.replace('T', ' ').slice(0, 19) : '';

export const findStopTerminalStatuses = (
  terminalStatusesByStop: TerminalStatusWrapperDTO[],
  stopId: number,
): TerminalStatusBriefDTO[] =>
  terminalStatusesByStop.find((wrapper) => wrapper.stopId === stopId)?.statuses ?? [];

export const getLastLoginTooltip = (insertDate: string): string =>
  insertDate ? getTimeDiff(insertDate).diff : '';

export const getPlannedNextConnectionTooltip = (
  plannedNextConnection: string,
  t: TranslationFunction,
): string => {
  if (!plannedNextConnection) {
    return '';
  }

  const { diff, diffMin } = getTimeDiff(plannedNextConnection);
  const isLate = isLateLevel(getCommunicationLevel(diffMin));
  const prefix = isLate ? '' : t('stop-map.info.modal.thead.remaining.text-pre');
  const suffix = isLate
    ? t('stop-map.info.modal.thead.delay.text')
    : t('stop-map.info.modal.thead.remaining.text');

  return `${prefix}${diff} ${suffix}`;
};

export const getCommunicationStatus = (
  plannedNextConnection: string,
  t: TranslationFunction,
): CommunicationStatus => {
  const { diffMin } = getTimeDiff(plannedNextConnection);

  if (!diffMin) {
    return {};
  }

  const level = getCommunicationLevel(diffMin);

  return {
    level,
    isLate: isLateLevel(level),
    color: COMMUNICATION_COLOR[level],
    text: t(COMMUNICATION_TEXT_KEY[level]),
  };
};
